import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import ErrorBoundary from './pages/ErrorBoundary.tsx';

// Add version timestamp for cache busting
const appVersion = new Date().toISOString();
const buildTimestamp = import.meta.env.BUILD_TIMESTAMP || Date.now();

// Add version to window for debugging
window.__APP_VERSION__ = appVersion;
// Add build timestamp to localStorage for cache detection
localStorage.setItem('buildTimestamp', buildTimestamp.toString());

// Check if there's a newer version by comparing timestamps
const storedTimestamp = localStorage.getItem('lastBuildTimestamp');
if (storedTimestamp && parseInt(storedTimestamp) !== buildTimestamp) {
  localStorage.setItem('lastBuildTimestamp', buildTimestamp.toString());
  // Use setTimeout to ensure this message is seen before reload
  setTimeout(() => window.location.reload(), 500);
} else {
  localStorage.setItem('lastBuildTimestamp', buildTimestamp.toString());
}

// Force reload if this is an old version (for external previews)
const lastLoadTime = sessionStorage.getItem('lastLoadTime');
const currentTime = Date.now();
sessionStorage.setItem('lastLoadTime', currentTime.toString());

// If page was loaded more than 3 minutes ago, force refresh
if (lastLoadTime && (currentTime - parseInt(lastLoadTime)) > 3 * 60 * 1000) {
  window.location.reload();
}

// Handle errors during initial load
window.addEventListener('error', (event) => {
  // If we have load errors, try clearing cache and reloading
  if (document.readyState !== 'complete') {
    sessionStorage.clear();
    localStorage.clear();
    
    // Add a flag to prevent infinite reload loops
    if (!sessionStorage.getItem('attempted_reload')) {
      sessionStorage.setItem('attempted_reload', 'true');
      window.location.reload();
    }
  }
});

// Add a special event listener for detecting stale resources
window.addEventListener('load', () => {
  // Check if any resources were loaded from cache
  const resources = performance.getEntriesByType('resource');
  const cachedResources = resources.filter(r => {
    // Check for resources that loaded suspiciously fast (likely from cache)
    return r.duration < 5 && (r.name.includes('.js') || r.name.includes('.css'));
  });
  
  if (cachedResources.length > 0) {
    // If we have cached resources and haven't tried a reload yet, force one
    if (!sessionStorage.getItem('cache_cleared')) {
      sessionStorage.setItem('cache_cleared', 'true');
      // Use setTimeout to ensure this message is seen
      setTimeout(() => window.location.reload(), 300);
    }
  }
});

// Create root only once
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");
const root = createRoot(rootElement);

// Wrap in ErrorBoundary and StrictMode, then render app
root.render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
